import React, { useState, useCallback } from 'react';
import JobInput from './JobInput';
import WellSelection from './WellSelection';
import WaterSourceSelection from './WaterSourceSelection';
import ChemicalDisclosure from './ChemicalDisclosure';
import MultiWellChemicalDisclosure from './MultiWellChemicalDisclosure';
import XmlGenerator from './XmlGenerator';
import MultiWellXmlGenerator from './MultiWellXmlGenerator';
import './XmlCreator.css';

const XmlCreator = () => {
  // Application state
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [wellsData, setWellsData] = useState([]);
  const [selectedWells, setSelectedWells] = useState([]);
  const [waterSource, setWaterSource] = useState({ type: 'single', source: '' });
  const [wellIngredients, setWellIngredients] = useState({}); // Store ingredients per well

  // Handle data fetched from JobInput
  const handleDataFetched = (data) => {
    setWellsData(data);
    setCurrentStep(2); // Move to well selection step
  };

  // Handle loading state
  const handleLoading = (loading) => {
    setIsLoading(loading);
  };

  // Handle well selection
  const handleWellSelected = (wells) => {
    // Handle both single well (legacy) and multiple wells
    const wellsArray = Array.isArray(wells) ? wells : [wells];
    setSelectedWells(wellsArray);
    setCurrentStep(3); // Move to water source selection step
  };

  // Handle water source selection
  const handleWaterSourceSelected = (source) => {
    console.log('XmlCreator - Received water source:', source);
    console.log('XmlCreator - Water source type:', typeof source);
    console.log('XmlCreator - Water source JSON:', JSON.stringify(source, null, 2));
    setWaterSource(source);
    setCurrentStep(4); // Move to chemical disclosure step
  };

  // Handle ingredients update for a specific well
  const handleIngredientsChange = useCallback((wellName, updatedIngredients) => {
    setWellIngredients(prev => ({
      ...prev,
      [wellName]: updatedIngredients
    }));
  }, []);

  // Handle chemical disclosure completion for single well
  const handleDisclosureComplete = (updatedWellData, updatedIngredients) => {
    // Update the well ingredients for the specific well
    const wellName = updatedWellData.wellName || updatedWellData.name;
    setWellIngredients(prev => ({
      ...prev,
      [wellName]: updatedIngredients
    }));
    
    // Update the well data in selectedWells array
    setSelectedWells(prev => prev.map(well => 
      (well.wellName || well.name) === wellName ? updatedWellData : well
    ));
    
    setCurrentStep(5); // Move to XML generation step
  };

  // Handle chemical disclosure completion for multiple wells
  const handleMultiWellDisclosureComplete = (updatedWells, updatedWellIngredients) => {
    // Update all wells and their ingredients
    setSelectedWells(updatedWells);
    setWellIngredients(updatedWellIngredients);
    setCurrentStep(5); // Move to XML generation step
  };


  // Handle navigation to a specific step
  const navigateToStep = (step) => {
    // Validate navigation based on data availability
    if (step === 1) {
      // Always allow navigation to step 1
      setCurrentStep(1);
    } else if (step === 2 && wellsData.length > 0) {
      // Allow navigation to step 2 if wells data is available
      setCurrentStep(2);
    } else if (step === 3 && selectedWells.length > 0) {
      // Allow navigation to step 3 if wells are selected
      setCurrentStep(3);
    } else if (step === 4 && waterSource) {
      // Allow navigation to step 4 if water source is selected
      setCurrentStep(4);
    }
    // Removed direct navigation to step 5 - must use "Proceed to XML Generation" button
    // which performs validation before proceeding
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <JobInput 
            onDataFetched={handleDataFetched} 
            onLoading={handleLoading} 
          />
        );
      case 2:
        return (
          <WellSelection 
            jobData={wellsData} 
            onWellSelected={handleWellSelected}
            setLoading={handleLoading}
          />
        );
      case 3:
        return (
          <WaterSourceSelection 
            wellData={selectedWells[0]} // Use first well for water source selection
            onWaterSourceSelected={handleWaterSourceSelected}
          />
        );
      case 4:
        // Use MultiWellChemicalDisclosure for multiple wells, ChemicalDisclosure for single well
        if (selectedWells.length > 1) {
          return (
            <MultiWellChemicalDisclosure 
              selectedWells={selectedWells}
              waterSource={waterSource}
              wellIngredients={wellIngredients}
              onIngredientsChange={handleIngredientsChange}
              onComplete={handleMultiWellDisclosureComplete}
            />
          );
        } else {
          return (
            <ChemicalDisclosure 
              wellData={selectedWells[0]}
              waterSource={waterSource}
              onIngredientsChange={(ingredients) => handleIngredientsChange(selectedWells[0]?.wellName || selectedWells[0]?.name, ingredients)}
              onComplete={handleDisclosureComplete}
            />
          );
        }
      case 5:
        // Use MultiWellXmlGenerator for multiple wells, XmlGenerator for single well
        if (selectedWells.length > 1) {
          return (
            <MultiWellXmlGenerator 
              selectedWells={selectedWells}
              wellIngredients={wellIngredients}
              waterSources={waterSource}
              onBack={() => setCurrentStep(4)}
            />
          );
        } else {
          return (
            <XmlGenerator 
              wellData={selectedWells[0]}
              chemicalData={wellIngredients[selectedWells[0]?.wellName || selectedWells[0]?.name] || []}
              waterSources={waterSource}
              onBack={() => setCurrentStep(4)}
            />
          );
        }
      default:
        return <div>Unknown step</div>;
    }
  };

  // Steps for the sidebar
  const steps = [
    { id: 1, name: 'Job Input', enabled: true },
    { id: 2, name: 'Well Selection', enabled: wellsData.length > 0 },
    { id: 3, name: 'Water Source', enabled: selectedWells.length > 0 },
    { id: 4, name: 'Chemical Disclosure', enabled: !!waterSource.source || (waterSource.type === 'multiple' && waterSource.sources && waterSource.sources.length > 0) },
    { id: 5, name: 'Generate XML', enabled: currentStep === 5 } // Only enabled when already on step 5
  ];

  return (
    <div className="xml-creator-container">
      <div className="xml-creator-progress">
        <div className="progress-header">
          <h3>Progress</h3>
        </div>
        <ul className="progress-steps">
          {steps.map((step) => (
            <li 
              key={step.id} 
              className={`progress-step ${currentStep === step.id ? 'active' : ''} ${step.enabled ? 'enabled' : 'disabled'}`}
              onClick={() => step.enabled && navigateToStep(step.id)}
            >
              <div className="step-number">{step.id}</div>
              <div className="step-content">
                <span className="step-name">{step.name}</span>
                {currentStep === step.id && (
                  <span className="step-status">Current</span>
                )}
                {currentStep > step.id && step.enabled && (
                  <span className="step-status completed">Completed</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <main className={`xml-creator-content ${isLoading ? 'loading' : ''}`}>
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading data...</p>
          </div>
        )}
        {renderStepContent()}
      </main>
    </div>
  );
};

export default XmlCreator;
